/* eslint-disable react/no-unused-prop-types */
import { faBed } from '@fortawesome/pro-regular-svg-icons/faBed';
import { faBell } from '@fortawesome/pro-regular-svg-icons/faBell';
import { faHomeLg } from '@fortawesome/pro-regular-svg-icons/faHomeLg';
import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import { faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons/faMapMarkerAlt';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faSlidersH } from '@fortawesome/pro-regular-svg-icons/faSlidersH';
import { faTag } from '@fortawesome/pro-regular-svg-icons/faTag';
import { faThLarge } from '@fortawesome/pro-regular-svg-icons/faThLarge';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import {
  CATEGORY_OPTIONS,
  CURRENCY_OPTIONS,
  EXPAND_OPTIONS,
  HAS_BEDROOMS_FILTER,
  PRICE_OPTIONS,
  RADIUS_OPTIONS,
  RENTAL_PRICE_OPTIONS,
} from '@propertypal/shared/src/constants/filterOptions';
import { RecentSearch } from '@propertypal/shared/src/hooks/useRecentSearches';
import { UseSearchState } from '@propertypal/shared/src/hooks/useSearchState';
import { selectProperties } from '@propertypal/shared/src/reducers/properties';
import { initialState, loadSavedSearch, SearchState, setSearch } from '@propertypal/shared/src/reducers/search';
import { useAppDispatch } from '@propertypal/shared/src/reducers/store';
import filterIcon from '@propertypal/shared/src/resources/icons/search/filter-icon-black.svg?url';
import filterOrangeIcon from '@propertypal/shared/src/resources/icons/search/filter-icon-orange.svg?url';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { PropertySearchResult } from '@propertypal/shared/src/types/propertySearch';
import { usePropertyStyles } from '@propertypal/shared/src/utils/property/getPropertyStyles';
import { getCategoryOption, getPriceSuffix } from '@propertypal/shared/src/utils/search/category';
import { useFilters } from '@propertypal/shared/src/utils/search/getFilters';
import getFilterTitles, { getTotalFilters } from '@propertypal/shared/src/utils/search/getFilterTitles';
import { Filters } from '@propertypal/shared/src/utils/search/getNlpFilters';
import { Suggestion } from '@propertypal/shared/src/utils/search/groupTokensByType';
import Button from '@propertypal/web-ui/src/buttons/Button';
import DropButton from '@propertypal/web-ui/src/buttons/DropButton';
import ListButtons, { LinkType } from '@propertypal/web-ui/src/buttons/ListButtons';
import Tag from '@propertypal/web-ui/src/buttons/Tag';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import CheckItem from '@propertypal/web-ui/src/inputs/CheckItem';
import SearchInput from '@propertypal/web-ui/src/inputs/SearchInput';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import { Column, Row, RowCenter, RowWrap } from '@propertypal/web-ui/src/layout';
import useMediaQuery from '@propertypal/web-ui/src/media-query/useMediaQuery';
import { closeTooltip, openTooltip } from '@propertypal/web-ui/src/notifications/Tooltip';
import { InputLabel } from '@propertypal/web-ui/src/typography';
import { useRouter } from 'next/router';
import React, { FormEvent, FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import getCurrentLocation from '../../../services/location';
import togglePageScroll from '../../../utils/togglePageScroll';
import BedInputs from './BedInputs';
import IncludeExcludeInputs from './IncludeExcludeInputs';
import PreferenceInputs from './PreferenceInputs';
import PriceInputs from './PriceInputs';
import {
  SearchBarForm,
  SearchModal,
  InputContainer,
  InputRow,
  InputColumn,
  FilterContainer,
  FiltersPane,
  FiltersButton,
  ModalFooter,
  CloseIcon,
  ToggleView,
  ScrollDiv,
  filterBreakpoint,
  CurrencyToggle,
  selectStyle,
  SmartFiltersWrapper,
  FiltersContent,
  FilterPaneContent,
  SaveSearchWrapper,
} from './SearchBar.style';
import SmartInputs from './SmartInputs';
import StyleInputs from './StyleInputs';

interface Props {
  searchModalOpen: boolean;
  onSearchModalToggle: (open: boolean) => void;
  loading: boolean;
  imageLocator: string;
  searchState: UseSearchState;
  searchUrl?: string;
  onSubmit: () => void;
  onSaveSearchClick: () => void;
  onDrawSearchClick?: () => void;
  onTravelTimeClick: () => void;
  listStyle: string;
  onListStyleUpdate: (listStyle: string) => void;
  recentSearches: RecentSearch[];
  removeRecentSearch: (search: RecentSearch) => void;
  loggedIn: boolean;
  onCloseToSearch: (polyIds: number[]) => Promise<boolean>;
  importantPlaces?: PropertySearchResult['importantPlaces'];
}

const currencyInput = (filters: SearchState['filters'], handleFilterChange: UseSearchState['handleFilterChange']) => {
  return (
    <CurrencyToggle
      label="Currency"
      options={CURRENCY_OPTIONS}
      onChange={handleFilterChange('currency')}
      value={filters.currency || 'GBP'}
    />
  );
};

const furnishingInputs = (
  filters: SearchState['filters'],
  handleFilterChange: UseSearchState['handleFilterChange'],
  isMobile?: boolean,
) => {
  return (
    <>
      <InputColumn>
        <CheckItem
          ariaLabel="Filter by unfurnished properties"
          containerStyle={{ marginRight: 20 }}
          label="Unfurnished"
          checked={filters.unfurnished}
          onValueChange={handleFilterChange('unfurnished')}
          testID="checkItem-unfurnished"
        />

        <CheckItem
          ariaLabel="Filter by furnished properties"
          containerStyle={{ marginRight: isMobile ? 0 : 20 }}
          label="Furnished"
          checked={filters.furnished}
          onValueChange={handleFilterChange('furnished')}
          testID="checkItem-furnished"
        />
      </InputColumn>

      <InputColumn>
        <CheckItem
          ariaLabel="Filter by partially furnished properties"
          containerStyle={{ marginRight: 20 }}
          label="Partially Furnished"
          checked={filters.partiallyFurnished}
          onValueChange={handleFilterChange('partiallyFurnished')}
          testID="checkItem-partially-furnished"
        />

        <CheckItem
          ariaLabel="Filter by optionally furnished properties"
          containerStyle={{ marginRight: isMobile ? 0 : 20 }}
          label="Optional"
          checked={filters.optionalFurnished}
          onValueChange={handleFilterChange('optionalFurnished')}
          testID="checkItem-optional-furnished"
        />
      </InputColumn>
    </>
  );
};

const styleTags = (filters: SearchState['filters'], handleClearAttribute: UseSearchState['handleClearAttribute']) => {
  if (filters.filter.length > 0) {
    return (
      <>
        <InputLabel mt={20}>Additional filters</InputLabel>

        <RowWrap>
          {filters.filter.map(({ description, filterValue }) => (
            <Tag
              containerStyle={{ marginRight: 15, marginBottom: 5 }}
              onCloseClick={() => handleClearAttribute(filterValue)}
            >
              {description}
            </Tag>
          ))}
        </RowWrap>
      </>
    );
  }

  return null;
};

const SearchBar: FunctionComponent<Props> = (props) => {
  const {
    disableSaveSearch,
    isSavable,
    searchText,
    appFilters,
    showExpand,
    showRadius,
    nlpData,
    nlpFilters,
    handleClearAttribute,
    handleChangeText,
    handleFilterChange,
    handleStyleChange,
    handleClearFilters,
    getLocation,
    fetchingLocation,
  } = props.searchState;

  const properties = useSelector(selectProperties);
  const isTablet = useMediaQuery({ query: `(${filterBreakpoint})` });
  const theme = useTheme();
  const router = useRouter();
  const dispatch = useDispatch();
  const asyncDispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeFilterPane, setActiveFilterPane] = useState('');
  const filters = { ...appFilters, ...nlpFilters };
  const filterQuery = useFilters(filters, false);
  const { priceText, bedsText, stylesText, moreText } = getFilterTitles(filters);
  const totalFilters = getTotalFilters(appFilters, true);

  const propertyStyles = usePropertyStyles(filters.category, filters.saleType);
  const currencySymbol = filters.currency === 'EUR' ? '€' : '£';
  const isRental = filters.saleType === 'rent';
  const isNewHomes = filters.category === 'newhomes';
  const isIE = nlpData?.phrase.roi;
  const showFurnishingOptions = ['residential', 'student'].includes(filters.category) && filters.saleType === 'rent';
  const [minPriceSuffix, fullMinPriceSuffix] = getPriceSuffix(filters, 'min', 'minimum');
  const [maxPriceSuffix, fullMaxPriceSuffix] = getPriceSuffix(filters, 'max', 'maximum');

  const listButtonsLinks = [
    {
      text: 'List',
      icon: faList,
      url: props.searchUrl || '/search',
      active: router.pathname === '/search' && props.listStyle === 'list',
    },
    {
      text: 'Grid',
      icon: faThLarge,
      url: props.searchUrl || '/search',
      active: router.pathname === '/search' && props.listStyle === 'grid',
      hideOnMobile: true,
    },
    {
      text: 'Map',
      icon: faMapMarkerAlt,
      url: `/map${props.searchUrl || ''}`,
      active: router.pathname === '/map',
    },
  ];

  const handleLinkButtonClick = (linkButton: LinkType) => {
    const currentDisplay = router.pathname === '/map' ? 'Map' : props.listStyle === 'grid' ? 'Grid' : 'List';
    trackGaEvent('search_view_change', {
      change: `${currentDisplay.toLowerCase()}_to_${linkButton.text.toLowerCase()}`,
      view: linkButton.text.toLowerCase(),
    });

    if (linkButton.text === 'List') {
      props.onListStyleUpdate('list');
    } else if (linkButton.text === 'Grid') {
      props.onListStyleUpdate('grid');
    }

    if (linkButton.text === 'Map') {
      router.push(`/map${props.searchUrl || ''}`);
    } else if (router.pathname !== '/search') {
      router.push(props.searchUrl || '/search');
    }
  };

  const minPriceOptions = isRental
    ? RENTAL_PRICE_OPTIONS(currencySymbol, minPriceSuffix, fullMinPriceSuffix)
    : PRICE_OPTIONS(currencySymbol, minPriceSuffix, fullMinPriceSuffix);
  const maxPriceOptions = isRental
    ? RENTAL_PRICE_OPTIONS(currencySymbol, maxPriceSuffix, fullMaxPriceSuffix)
    : PRICE_OPTIONS(currencySymbol, maxPriceSuffix, fullMaxPriceSuffix);

  const toggleFilterPane = (filterPane: string) => {
    if (filterPane === activeFilterPane) {
      setActiveFilterPane('');
    } else {
      setActiveFilterPane(filterPane);
    }
  };

  const handleSubmit = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    inputRef.current?.blur();
    props.onSearchModalToggle(false);

    const latLng = await getLocation(getCurrentLocation, false);

    setActiveFilterPane('');
    dispatch(
      setSearch({
        text: searchText,
        filters: { ...appFilters, ...latLng },
      }),
    );
    props.onSubmit();
  };

  const handleSuggestionClick = (token: Suggestion) => {
    if (token.searchParams) {
      asyncDispatch(loadSavedSearch(token.searchParams, props.imageLocator));
    } else if (token.url) {
      router.push(token.url);
    } else {
      handleChangeText(token.text);
    }
  };

  const handleRecentSearch = (text: string, recentFilters: Filters) => {
    trackGaEvent('recent_search_click', { location: 'Search Bar' });

    dispatch(
      setSearch({
        text,
        filters: { ...initialState.filters, ...recentFilters },
      }),
    );
    props.onSubmit();
  };

  const handleDrawSearch = (position: 'suggestion' | 'inline') => {
    if (props.onDrawSearchClick) {
      trackGaEvent('draw_click', {
        page: 'SRP',
        location: 'Search Box',
        position,
      });
      props.onDrawSearchClick();
    }
  };

  useEffect(() => {
    if (props.searchModalOpen) {
      togglePageScroll('#searchModal', false);
    } else {
      togglePageScroll('#searchModal', true);
    }
  }, [props.searchModalOpen]);

  useEffect(() => {
    if (!isTablet && props.searchModalOpen) {
      props.onSearchModalToggle(false);
    }
  }, [isTablet]);

  useEffect(() => {
    return () => togglePageScroll('#searchModal', true);
  }, []);

  return (
    <form action="" onSubmit={handleSubmit} role="search">
      <SearchBarForm>
        <InputContainer>
          <SearchInput
            inputRef={inputRef}
            onChangeText={handleChangeText}
            value={searchText}
            tokens={nlpData?.phrase.words}
            recentSearches={props.recentSearches}
            onRemoveRecentSearch={props.removeRecentSearch}
            onRecentSearchClick={handleRecentSearch}
            nlpData={nlpData}
            loading={fetchingLocation}
            onSuggestionClick={handleSuggestionClick}
            onLocationClick={(position) => {
              trackGaEvent('current_location_click', {
                page: 'SRP',
                location: 'Search Box',
                position,
              });
              getLocation(getCurrentLocation, true);
            }}
            onTravelTimeClick={(position) => {
              trackGaEvent('travel_time_click', {
                page: 'SRP',
                location: 'Search Box',
                position,
              });
              props.onTravelTimeClick();
            }}
            onDrawSearchClick={props.onDrawSearchClick ? handleDrawSearch : undefined}
          />

          {showRadius && !isTablet && (
            <SelectInput
              ariaLabel="Select a search radius"
              containerStyle={{
                maxWidth: 165,
                marginRight: 10,
                borderLeft: `1px solid ${theme.backgroundLighter}`,
              }}
              inputStyle={selectStyle}
              id="selectRadius"
              disableMaxHeight
              placeholder="Radius"
              options={RADIUS_OPTIONS}
              value={filters.radius}
              onChange={handleFilterChange('radius')}
              testID="selectRadius"
            />
          )}

          {showExpand && !isTablet && (
            <SelectInput
              ariaLabel="Expand search radius"
              containerStyle={{
                maxWidth: 165,
                marginRight: 10,
                borderLeft: `1px solid ${theme.backgroundLighter}`,
              }}
              inputStyle={selectStyle}
              id="expandRadius"
              disableMaxHeight
              placeholder="Expand"
              options={EXPAND_OPTIONS}
              value={filters.expand}
              onChange={handleFilterChange('expand')}
              testID="expandRadius"
            />
          )}

          <FiltersButton
            aria-label="Filters"
            active={filterQuery !== ''}
            onClick={() => props.onSearchModalToggle(!props.searchModalOpen)}
            data-testid="filtersButton"
            type="button"
          >
            {filterQuery === '' && (
              <img
                src={filterIcon.src}
                width={filterIcon.width}
                height={filterIcon.height}
                alt="filter icon"
                style={{ marginRight: 5 }}
              />
            )}
            {filterQuery !== '' && (
              <img
                src={filterOrangeIcon.src}
                width={filterOrangeIcon.width}
                height={filterOrangeIcon.height}
                alt="filter icon"
                style={{ marginRight: 5 }}
              />
            )}
            <span>Filters</span>
          </FiltersButton>
        </InputContainer>

        <Button
          ariaLabel="Search"
          color={theme.darkGrey}
          loading={props.loading}
          icon={faSearch}
          testID="searchSubmitButton"
          type="submit"
        >
          Search
        </Button>
      </SearchBarForm>

      {isTablet && (
        <SearchModal active={props.searchModalOpen} data-testid="search-modal">
          <ScrollDiv id="searchModal">
            {/* modal open check prevents contents being loaded initially. */}
            {/* delayed loading of style inputs helps page speed */}
            {props.searchModalOpen && (
              <>
                <CloseIcon
                  data-testid="closeSearchModalButton"
                  onClick={() => props.onSearchModalToggle(false)}
                  type="button"
                >
                  <FontAwesomeIcon icon={faTimes} color={theme.backgroundMid} style={{ fontSize: 30 }} />
                </CloseIcon>

                {showRadius && (
                  <InputRow>
                    <SelectInput
                      ariaLabel="Select a search radius"
                      id="selectRadiusSelect"
                      labelStyle={{ fontSize: 14, color: theme.primary }}
                      inputStyle={selectStyle}
                      placeholder="Search radius"
                      label="Radius"
                      options={RADIUS_OPTIONS}
                      value={filters.radius}
                      onChange={handleFilterChange('radius')}
                      testID="selectRadius"
                    />
                  </InputRow>
                )}

                {showExpand && (
                  <InputRow>
                    <SelectInput
                      ariaLabel="Expand search radius"
                      id="expandRadiusSelect"
                      labelStyle={{ fontSize: 14, color: theme.primary }}
                      inputStyle={selectStyle}
                      placeholder="Expand"
                      label="Expand"
                      options={EXPAND_OPTIONS}
                      value={filters.expand}
                      onChange={handleFilterChange('expand')}
                      testID="expandRadius"
                    />
                  </InputRow>
                )}

                <InputRow>
                  <InputColumn>
                    <SelectInput
                      ariaLabel="Select a category"
                      label="Select a category"
                      labelStyle={{ fontSize: 14, color: theme.primary }}
                      id="categorySelect"
                      inputStyle={selectStyle}
                      options={CATEGORY_OPTIONS}
                      value={getCategoryOption(filters)}
                      onChange={handleFilterChange('category')}
                      testID="searchCategory"
                    />

                    {currencyInput(filters, handleFilterChange)}
                  </InputColumn>
                </InputRow>

                <InputRow mt="0px">
                  <InputColumn>
                    <PriceInputs
                      filters={filters}
                      minPriceOptions={minPriceOptions}
                      maxPriceOptions={maxPriceOptions}
                      handleFilterChange={handleFilterChange}
                      currencySymbol={currencySymbol}
                      isMobile
                    />
                  </InputColumn>

                  {HAS_BEDROOMS_FILTER.includes(filters.category) && (
                    <InputColumn>
                      <BedInputs filters={filters} handleFilterChange={handleFilterChange} isMobile />
                    </InputColumn>
                  )}
                </InputRow>

                <div style={{ marginTop: 30 }}>
                  <StyleInputs
                    filters={filters}
                    propertyStyles={propertyStyles}
                    handleStyleChange={handleStyleChange}
                    isMobile
                  />
                </div>

                <div style={{ marginTop: 30 }}>
                  <IncludeExcludeInputs
                    filters={filters}
                    handleFilterChange={handleFilterChange}
                    isRental={isRental}
                    isMobile
                  />
                </div>

                {showFurnishingOptions && (
                  <>
                    <Row style={{ width: '100%', marginTop: 20 }}>
                      <InputLabel>Furnishing</InputLabel>
                    </Row>

                    <InputRow mt="0px">{furnishingInputs(filters, handleFilterChange, true)}</InputRow>
                  </>
                )}

                {styleTags(filters, handleClearAttribute)}

                {['residential', 'newhomes', 'student', 'holidayhomes'].includes(filters.category) && (
                  <>
                    <SmartInputs
                      filters={filters}
                      handleFilterChange={handleFilterChange}
                      isNewHomes={isNewHomes}
                      searchParams={properties.data?.params}
                      isIE={isIE}
                      isMobile
                    />

                    <PreferenceInputs
                      filters={filters}
                      handleFilterChange={handleFilterChange}
                      isRental={isRental}
                      isNewHomes={isNewHomes}
                    />
                  </>
                )}
              </>
            )}
          </ScrollDiv>

          <ModalFooter>
            <Button
              containerStyle={{ maxWidth: 160, height: 44 }}
              color={theme.backgroundDark}
              onClick={handleClearFilters}
              type="button"
            >
              Reset Filters {totalFilters > 0 ? `(${totalFilters})` : ''}
            </Button>

            <Button containerStyle={{ marginLeft: 15, maxWidth: 160, height: 44 }} type="submit">
              Apply Filters
            </Button>
          </ModalFooter>
        </SearchModal>
      )}

      <FilterContainer role="menubar">
        <FiltersContent>
          <DropButton
            active={activeFilterPane === 'price'}
            ariaHasPopup
            ariaExpanded={activeFilterPane === 'price'}
            text={priceText}
            icon={faTag}
            iconActive={priceText !== 'Price Range'}
            onClick={() => toggleFilterPane('price')}
            role="menuitem"
            testID="priceRangeDropdown"
          />

          {HAS_BEDROOMS_FILTER.includes(filters.category) && (
            <DropButton
              active={activeFilterPane === 'bedrooms'}
              ariaHasPopup
              ariaExpanded={activeFilterPane === 'bedrooms'}
              text={bedsText}
              icon={faBed}
              iconActive={bedsText !== 'Bedrooms'}
              onClick={() => toggleFilterPane('bedrooms')}
              role="menuitem"
              testID="bedroomsDropdown"
            />
          )}

          {propertyStyles && propertyStyles.length > 0 && (
            <DropButton
              active={activeFilterPane === 'propertyType'}
              ariaHasPopup
              ariaExpanded={activeFilterPane === 'propertyType'}
              text={stylesText}
              icon={faHomeLg}
              iconActive={stylesText !== 'Property Type'}
              onClick={() => toggleFilterPane('propertyType')}
              role="menuitem"
              testID="propertyTypeDropdown"
            />
          )}

          <DropButton
            active={activeFilterPane === 'more'}
            ariaHasPopup
            ariaExpanded={activeFilterPane === 'more'}
            text={moreText}
            icon={faSlidersH}
            iconActive={moreText !== 'More Filters'}
            onClick={() => toggleFilterPane('more')}
            role="menuitem"
            testID="moreFiltersDropdown"
          />

          <ToggleView>
            <ListButtons links={listButtonsLinks} onClick={handleLinkButtonClick} />
          </ToggleView>

          {(isSavable || properties.loading) && (
            <SaveSearchWrapper
              onMouseEnter={(e) => {
                if (disableSaveSearch) {
                  openTooltip(
                    'You must click the "Search" button in the search form to update your results before you can Save this search.',
                    e,
                  );
                }
              }}
              onMouseLeave={closeTooltip}
            >
              <Button
                containerStyle={{ height: 54 }}
                icon={faBell}
                onClick={props.onSaveSearchClick}
                disabled={disableSaveSearch}
                type="button"
              >
                {properties.data?.savedSearch ? 'Edit' : 'Save'} Search
              </Button>
            </SaveSearchWrapper>
          )}
        </FiltersContent>

        {activeFilterPane === 'price' && (
          <FiltersPane>
            <FilterPaneContent>
              {currencyInput(filters, handleFilterChange)}

              <PriceInputs
                filters={filters}
                minPriceOptions={minPriceOptions}
                maxPriceOptions={maxPriceOptions}
                handleFilterChange={handleFilterChange}
                currencySymbol={currencySymbol}
              />
            </FilterPaneContent>
          </FiltersPane>
        )}

        {activeFilterPane === 'bedrooms' && (
          <FiltersPane>
            <FilterPaneContent>
              <BedInputs filters={filters} handleFilterChange={handleFilterChange} />
            </FilterPaneContent>
          </FiltersPane>
        )}

        {activeFilterPane === 'propertyType' && (
          <FiltersPane>
            <FilterPaneContent style={{ flexDirection: 'column' }}>
              <StyleInputs filters={filters} propertyStyles={propertyStyles} handleStyleChange={handleStyleChange} />
            </FilterPaneContent>
          </FiltersPane>
        )}

        {activeFilterPane === 'more' && (
          <FiltersPane $overflow>
            <FilterPaneContent>
              <Row flexWrap="wrap">
                <IncludeExcludeInputs
                  filters={filters}
                  isRental={isRental}
                  handleFilterChange={handleFilterChange}
                  isMobile={false}
                />
              </Row>

              {showFurnishingOptions && (
                <>
                  <Row style={{ width: '100%', marginTop: 20 }}>
                    <InputLabel>Furnishing</InputLabel>
                  </Row>

                  <Row style={{ minWidth: 960 }}>{furnishingInputs(filters, handleFilterChange)}</Row>
                </>
              )}

              <Column style={{ width: '100%' }}>{styleTags(filters, handleClearAttribute)}</Column>

              {['residential', 'newhomes', 'student', 'holidayhomes'].includes(filters.category) && (
                <>
                  <SmartFiltersWrapper>
                    <SmartInputs
                      filters={filters}
                      handleFilterChange={handleFilterChange}
                      isNewHomes={isNewHomes}
                      searchParams={properties.data?.params}
                      isIE={isIE}
                    />
                  </SmartFiltersWrapper>

                  <PreferenceInputs
                    filters={filters}
                    handleFilterChange={handleFilterChange}
                    isRental={isRental}
                    isNewHomes={isNewHomes}
                  />
                </>
              )}

              <RowCenter w="100%" mt={20}>
                <Button
                  containerStyle={{ maxWidth: 160, height: 44 }}
                  color={theme.backgroundLight}
                  hoverTextColor={theme.white}
                  fontColor={theme.backgroundMid}
                  onClick={handleClearFilters}
                  type="button"
                >
                  Reset Filters
                </Button>

                <Button containerStyle={{ marginLeft: 15, maxWidth: 160, height: 44 }} type="submit">
                  Apply Filters
                </Button>
              </RowCenter>
            </FilterPaneContent>
          </FiltersPane>
        )}
      </FilterContainer>
    </form>
  );
};

export default SearchBar;
